import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import AdminGate from "@/components/AdminGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export const Route = createFileRoute("/admin/peo-partners")({
  head: () => ({
    meta: [
      { title: "PEO partners — Admin" },
      { name: "description", content: "Manage PEO partners, client companies and portal access." },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "PEO partners — Admin" },
      { property: "og:description", content: "Manage PEO partners and client companies." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: () => <AdminGate><AdminPeo /></AdminGate>,
});

type Company = { id: string; name: string; code: string | null; is_active: boolean; hr: string[] };
type Peo = { id: string; slug: string; name: string; staff: string[]; companies: Company[] };

function AdminPeo() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["admin-peo"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("admin_list_peo");
      if (error) throw error;
      return (data as unknown as Peo[]) ?? [];
    },
  });
  const [emails, setEmails] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState<string | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-peo"] });

  const addMember = async (key: string, role: "peo_staff" | "employer_hr", peoId?: string, orgId?: string) => {
    const email = (emails[key] || "").trim();
    if (!email) return;
    const { data, error } = await supabase.rpc("admin_add_partner_member", { _email: email, _role: role, _peo_id: peoId, _org_id: orgId });
    setMsg(error ? "Couldn't add access." : data === "no_account" ? `${email} has no account yet. Ask them to create one first.` : `Access added for ${email}.`);
    setEmails((e) => ({ ...e, [key]: "" }));
    refresh();
  };

  if (q.isLoading) return <div className="p-10 text-muted-foreground">Loading…</div>;
  return (
    <main className="mx-auto max-w-5xl px-5 py-10">
      <h1 className="text-3xl font-bold text-foreground">PEO partners</h1>
      {msg && <p className="mt-3 rounded bg-muted p-3 text-sm text-foreground">{msg}</p>}
      {(q.data ?? []).map((p) => (
        <section key={p.id} className="mt-8 rounded-xl border bg-card p-5">
          <h2 className="text-xl font-semibold text-foreground">{p.name}</h2>
          <p className="text-sm text-muted-foreground">Join link: /{p.slug} · Portal: /partner/{p.slug}</p>
          <p className="mt-3 text-sm text-foreground">Staff: {p.staff.length ? p.staff.join(", ") : "none yet"}</p>
          <div className="mt-2 flex gap-2">
            <Input placeholder="Staff email" value={emails[p.id] || ""} onChange={(e) => setEmails({ ...emails, [p.id]: e.target.value })} />
            <Button onClick={() => addMember(p.id, "peo_staff", p.id)}>Give portal access</Button>
          </div>
          <div className="mt-6 space-y-4">
            {p.companies.map((c) => (
              <div key={c.id} className="rounded-lg border p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="font-medium text-foreground">{c.name}</p>
                    <p className="font-mono text-xs text-muted-foreground">{c.code} · {c.is_active ? "Active" : "Off"}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={async () => { await supabase.rpc("admin_set_org_active", { _org_id: c.id, _active: !c.is_active }); refresh(); }}>
                      {c.is_active ? "Turn off" : "Turn on"}
                    </Button>
                    <Button size="sm" variant="outline" onClick={async () => { if (confirm("Make a new code? The old code stops working.")) { await supabase.rpc("admin_regenerate_org_code", { _org_id: c.id }); refresh(); } }}>
                      New code
                    </Button>
                  </div>
                </div>
                <p className="mt-2 text-sm text-foreground">HR: {c.hr.length ? c.hr.join(", ") : "none yet"}</p>
                <div className="mt-2 flex gap-2">
                  <Input placeholder="HR email" value={emails[c.id] || ""} onChange={(e) => setEmails({ ...emails, [c.id]: e.target.value })} />
                  <Button size="sm" onClick={() => addMember(c.id, "employer_hr", undefined, c.id)}>Give HR access</Button>
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}
    </main>
  );
}
